import { Link } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, ImageOff, MessageCircle } from 'lucide-react';
import type { AdminCompanyHealth } from 'app/javascript/types/admin';
import { ROUTES } from 'app/javascript/constants/routes';
import { Badge } from 'app/javascript/components/ui/badge';
import { Button } from 'app/javascript/components/ui/button';
import { formatBrDate } from 'app/javascript/utils/dates';
import { buildWhatsAppUrl } from 'app/javascript/utils/whatsapp';

export default function CompanyHealthHeader({ company }: { company: AdminCompanyHealth }) {
  const whatsappUrl = buildWhatsAppUrl(company.phone, '');
  // One line of provenance, not a stat grid: where the academy is, who runs
  // it, since when. The numbers live in the panels below.
  const meta = [
    [company.city, company.state].filter(Boolean).join('/'),
    company.created_at && `desde ${formatBrDate(company.created_at.slice(0, 10))}`,
    company.owner_name && `dono: ${company.owner_name}`,
  ].filter(Boolean);

  return (
    <header className="space-y-4">
      <Link to={ROUTES.ADMIN} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="size-4" />
        Admin
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-bebas text-4xl italic tracking-tight text-yellow-400">{company.name}</h1>
            <Badge variant="outline">{company.kind}</Badge>
            {!company.logo_attached && (
              <Badge variant="outline" className="text-muted-foreground">
                <ImageOff className="size-3" />
                sem logo
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{meta.join(' · ')}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {whatsappUrl ? (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-sm text-yellow-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50"
              >
                <MessageCircle className="size-4" />
                WhatsApp público da academia: {company.phone}
              </a>
            ) : company.phone ? (
              `Telefone público: ${company.phone} (confira o número)`
            ) : (
              'Telefone público da academia não cadastrado'
            )}
          </p>
        </div>

        <Button asChild variant="outline" size="sm">
          <Link to={ROUTES.COMPANY_DETAILS} params={{ slug: company.slug }} target="_blank" rel="noreferrer noopener">
            <ExternalLink className="size-4" />
            Perfil público
          </Link>
        </Button>
      </div>
    </header>
  );
}
